import type { Direction } from '@/types/game'
import {
  buildGridState,
  cloneState,
  resolveSlide,
  findTile,
  collectTiles,
  type GridState,
  type Vec,
} from './slide'
import type { Tile } from './tiles'

const ALL_DIRS: Direction[] = ['up', 'down', 'left', 'right']

interface SearchNode {
  state: GridState
  pos: Vec
  slides: number
  coins: number
  path: Direction[]
}

function serialize(state: GridState, pos: Vec): string {
  let s = `${pos.x},${pos.y}|k${state.keys}|`
  for (let y = 0; y < state.rows; y++) s += state.cells[y].join('') + ';'
  return s
}

export interface SolveResult {
  solvable: boolean
  minSlides: number
  maxCoins: number
  bestPath: Direction[]
  coinsOnShortest: number
}

const UNSOLVABLE: SolveResult = {
  solvable: false,
  minSlides: 0,
  maxCoins: 0,
  bestPath: [],
  coinsOnShortest: 0,
}

export function solveLevel(grid: string[]): SolveResult {
  const base = buildGridState(grid)
  const startTile = findTile(base.cells, 'o')
  if (!startTile) return UNSOLVABLE
  base.cells[startTile.y][startTile.x] = '-'

  const start: SearchNode = { state: base, pos: startTile, slides: 0, coins: 0, path: [] }
  const queue: SearchNode[] = [start]
  let head = 0
  const seen = new Set<string>([serialize(base, startTile)])

  let minSlides = Infinity
  let bestPath: Direction[] = []
  let coinsOnShortest = 0
  let maxCoins = 0
  let iterations = 0
  const cap = 250000

  while (head < queue.length && iterations++ < cap) {
    const node = queue[head++]

    for (const dir of ALL_DIRS) {
      const nextState = cloneState(node.state)
      const res = resolveSlide(nextState, node.pos, dir)
      if (res.died) continue
      if (res.end.x === node.pos.x && res.end.y === node.pos.y && !res.reachedHome) continue

      let coinsGained = 0
      for (const ev of res.events) if (ev.kind === 'coin' || ev.kind === 'gem') coinsGained++
      const coins = node.coins + coinsGained
      const slides = node.slides + 1
      const path = [...node.path, dir]

      if (res.reachedHome) {
        if (slides < minSlides || (slides === minSlides && coins > coinsOnShortest)) {
          minSlides = slides
          bestPath = path
          coinsOnShortest = coins
        }
        if (coins > maxCoins) maxCoins = coins
        continue
      }

      const key = serialize(nextState, res.end)
      if (seen.has(key)) continue
      seen.add(key)
      queue.push({ state: nextState, pos: res.end, slides, coins, path })
    }
  }

  if (!Number.isFinite(minSlides)) return UNSOLVABLE
  return { solvable: true, minSlides, maxCoins: Math.max(maxCoins, coinsOnShortest), bestPath, coinsOnShortest }
}

export function hintNextSlide(cells: Tile[][], pos: Vec, keys: number): Direction | null {
  const state = buildGridState(cells.map((r) => r.join('')))
  state.keys = keys

  const start: SearchNode = { state, pos, slides: 0, coins: 0, path: [] }
  const queue: SearchNode[] = [start]
  let head = 0
  const seen = new Set<string>([serialize(state, pos)])
  let iterations = 0
  const cap = 150000

  while (head < queue.length && iterations++ < cap) {
    const node = queue[head++]
    for (const dir of ALL_DIRS) {
      const nextState = cloneState(node.state)
      const res = resolveSlide(nextState, node.pos, dir)
      if (res.died) continue
      if (res.end.x === node.pos.x && res.end.y === node.pos.y && !res.reachedHome) continue
      if (res.reachedHome) return node.path.length ? node.path[0] : dir
      const key = serialize(nextState, res.end)
      if (seen.has(key)) continue
      seen.add(key)
      queue.push({ state: nextState, pos: res.end, slides: node.slides + 1, coins: 0, path: [...node.path, dir] })
    }
  }
  return null
}

export function totalCollectibles(grid: string[]): number {
  const cells = grid.map((r) => r.split('') as Tile[])
  return collectTiles(cells, 'p').length + collectTiles(cells, 'g').length
}
